import { mString } from 'lib/helpers/utility';

import AddServiceActions from '../components/AddServiceActions';

const ZERO_VALUE = 0;
const DEFAULT_TIME_LENGTH = 60;
const DEFAULT_COLOR = '#000000';
const DEFAULT_CHARGE_TYPE = {
  value: 'self_pay',
  label: 'Self pay'
};

export const addServicesModalColumns = [
  {
    headerName: '',
    field: '',
    maxWidth: 40,
    headerCheckboxSelection: true,
    checkboxSelection: (params) => !params.node.group
  },
  {
    field: 'name',
    headerName: 'Name',
    sortable: false,
    flex: 1,
    valueGetter: (params) => params.data?.name || params.data?.cpt_description || ''
  },
  {
    field: 'code',
    headerName: 'Code',
    sortable: false,
    maxWidth: 120
  },
  {
    field: 'also_known_as',
    headerName: 'Also known as',
    sortable: false,
    flex: 1,
    valueGetter: (params) => params.data?.also_known_as || '-'
  },
  {
    field: 'self_pay',
    headerName: 'Self pay',
    cellRenderer: ({ value }) => mString(value || 0),
    sortable: false,
    maxWidth: 120
  },
  {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    maxWidth: 100,
    cellRenderer: AddServiceActions,
    pinned: 'right'
  }
];

export const applySelfPayFilter = (rows, selfPayFilters) => {
  let result = [...rows];
  if (selfPayFilters?.length > 0) {
    selfPayFilters.forEach((filter) => {
      const value = parseFloat(filter.values);
      if (!isNaN(value)) {
        result = result.filter((item) => {
          const itemValue = parseFloat(item.self_pay) || 0;
          if (filter.formula === 'is equal to') return itemValue === value;
          if (filter.formula === 'is less than') return itemValue < value;
          if (filter.formula === 'is more than') return itemValue > value;
          return true;
        });
      }
    });
  }
  return result;
};

export const computeServiceRows = (
  codes,
  { standardChargeMultiplier, selfPayMultiplier, localEdits }
) => {
  const standardPercentage = parseFloat(standardChargeMultiplier || 100) / 100;
  const selfPayPercentage = parseFloat(selfPayMultiplier || 100) / 100;

  return codes
    .filter((item) => (item.name || item.cpt_description || '').trim() !== '')
    .map((item) => {
      const edits = localEdits[item.code] || {};
      const merged = { ...item, ...edits };
      const effectiveParAmount = edits.par_amount ?? item.par_amount;
      return {
        ...merged,
        standard_charge: edits.standard_charge ?? Number(effectiveParAmount * standardPercentage),
        self_pay: edits.self_pay ?? Number(effectiveParAmount * selfPayPercentage),
        total_cost_cents:
          edits.total_cost_cents ?? Number((effectiveParAmount * selfPayPercentage).toFixed(0))
      };
    });
};

export const restructureScheduleOfFees = (data) =>
  data.map((item) => {
    const parAmount = item.par_amount || ZERO_VALUE;
    return {
      name: item.name || '',
      description: '',
      image: '',
      procedure_code: item.code || '',
      par_amount: parAmount,
      total_cost_cents: parAmount,
      time_length: DEFAULT_TIME_LENGTH,
      self_scheduling: false,
      charge_type: DEFAULT_CHARGE_TYPE.value,
      standard_charge: parAmount,
      cpt: null,
      dental_category: item.dental_category || null,
      paySchedule: [],
      serviceId: null,
      practitioner_ids: [],
      color: DEFAULT_COLOR,
      category_id: null,
      state_tax_rate: ZERO_VALUE,
      local_tax_rate: ZERO_VALUE,
      ndc_codes: [],
      professionGroups: item.professionGroups || []
    };
  });

export const buildServicesPayload = (rows, localEdits) =>
  rows.map((row) => ({
    ...restructureScheduleOfFees([row])[0],
    standard_charge: row.standard_charge,
    self_pay: row.self_pay,
    total_cost_cents: row.total_cost_cents,
    ...(localEdits[row.code] || {}),
    procedure_code: row.code
  }));

export const buildProfessionGroupFilter = (selectedSpecialty, specialties) => {
  if (selectedSpecialty === 'all') {
    if (specialties.length === 0) return [];
    return [{ values: specialties.map((specialty) => Number(specialty.id)) }];
  }
  return [{ values: [Number(selectedSpecialty)] }];
};

export const buildApiFilters = (professionGroupFilter, name) => {
  const result = {};
  if (professionGroupFilter.length > 0) {
    result.professionGroup = professionGroupFilter;
  }
  if (name?.length > 0) {
    result.name = name;
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

export const buildFooterActions = ({
  isCreateStep,
  isCreateFormChanged,
  isLoading,
  selectedRows,
  onGoBack,
  onSubmitForm,
  onAddServices
}) => {
  if (isCreateStep) {
    return {
      secondary: { text: 'Cancel', variant: 'tertiary', onClick: onGoBack, size: 'small' },
      primary: {
        text: 'Create new service',
        onClick: onSubmitForm,
        isLoading,
        isDisabled: !isCreateFormChanged,
        size: 'small'
      }
    };
  }
  return {
    primary: {
      onClick: onAddServices,
      text: selectedRows?.length
        ? `Add ${selectedRows.length} service${selectedRows.length !== 1 ? 's' : ''}`
        : 'Add services',
      isLoading,
      size: 'small',
      isDisabled: selectedRows.length === 0
    }
  };
};
